import React from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { ASSETS, LINKS, CA } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/PageHeader";
import { TermLabel } from "@/components/Effects";
import CaChip from "@/components/CaChip";
import { shortAddress } from "@/components/Wallet";

export default function Token() {
  const { t, lang } = useLanguage();
  const rows = t("token.rows");

  return (
    <div data-testid="token-page" className="relative">
      <PageHeader label={t("token.label")} title={t("token.title")} />

      <section className="mx-auto grid max-w-7xl items-start gap-10 px-4 pb-24 pt-12 sm:px-6 lg:grid-cols-[1fr_380px]">
        {/* Token data terminal */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="space-y-6"
        >
          <div className="mizu-panel mizu-corners p-6" data-testid="token-panel">
            <div className="mb-4 flex items-center justify-between border-b border-mizu-cyan/15 pb-2">
              <span className="font-mono text-[10px] tracking-[0.3em] text-mizu-cyan">
                {t("token.label")}
              </span>
              <span className="flex items-center gap-1.5 font-mono text-[10px] text-mizu-cyan">
                <span className="h-1.5 w-1.5 animate-pulse-glow bg-mizu-cyan" />
                {t("common.online")}
              </span>
            </div>
            {rows.map((r, i) => (
              <motion.div
                key={r.key}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.06 }}
                className="flex items-center justify-between border-b border-mizu-cyan/8 py-3 font-mono"
                data-testid={`token-row-${i}`}
              >
                <span className="text-[10px] tracking-[0.3em] text-mizu-faint">{r.key}</span>
                <span className={`text-xs tracking-[0.15em] ${r.key === "CA" ? "text-mizu-cyan mizu-glow" : "text-mizu-silver"}`}>
                  {r.key === "CA" ? shortAddress(CA) : r.value}
                </span>
              </motion.div>
            ))}
          </div>

          {/* CA panel */}
          <div
            className="mizu-panel mizu-corners border-mizu-cyan/30 p-6 text-center"
            data-testid="token-ca-panel"
          >
            <TermLabel className="justify-center">{t("token.caTitle")}</TermLabel>
            <div className="mt-4 flex justify-center">
              <CaChip full testid="token-ca-value" className="max-w-full" />
            </div>
            <p className="mt-3 font-mono text-[9px] tracking-[0.25em] text-mizu-faint">
              ARC MAINNET // CHAIN 5042
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <a
              href={LINKS.buy}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="token-buy-argus"
              className="mizu-btn flex items-center gap-2 border border-mizu-cyan bg-mizu-cyan/15 px-6 py-3 font-mono text-xs font-semibold tracking-[0.2em] text-white"
            >
              [ {t("token.buyOnArgus")} ] <ExternalLink size={11} />
            </a>
            <a
              href={LINKS.chart}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="token-view-chart"
              className="mizu-btn flex items-center gap-2 border border-mizu-cyan/40 px-6 py-3 font-mono text-xs tracking-[0.2em] text-mizu-cyan"
            >
              [ {t("token.viewChart")} ] <ExternalLink size={11} />
            </a>
          </div>

          <p className="max-w-xl border-l-2 border-mizu-cyan/30 pl-4 font-mono text-[10px] leading-6 text-mizu-faint" data-testid="token-disclaimer">
            {t("token.disclaimer")}
          </p>
        </motion.div>

        {/* Emblem side panel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mizu-corners relative hidden overflow-hidden border border-mizu-cyan/20 lg:block"
        >
          <img
            src={ASSETS.logo}
            alt="MIZUKARA emblem"
            data-testid="token-emblem"
            loading="lazy"
            className="w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020611]/70 via-transparent to-transparent" />
          <div className="absolute bottom-3 left-0 right-0 text-center font-mono text-[10px] tracking-[0.4em] text-mizu-cyan/80">
            MIZUKARA // ARC
          </div>
        </motion.div>
      </section>
    </div>
  );
}
